import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import adminService from '../../services/adminService';
import AuthContext from '../../contexts/AuthContext';
import { isToday, isPast } from '../../utils/dateUtils';
import { getAssignmentTitle } from '../../utils/assignmentUtils';
import '../../styles/AssignmentsListPage.css';

/**
 * AdminStudentAssignments - Assignment view for ADMINS
 * 
 * Used by:
 * - Admins: Viewing complete cross-class assignment data for any student
 * 
 * Features:
 * - Shows ALL assignments across ALL classes for a student
 * - Read-only view (no edit capabilities)
 * - Includes class context information
 * - Sorted newest first (by backend: createdAt descending)
 * - Uses dedicated admin service and endpoints
 * 
 * Part of the Pure Admin Architecture for reporting extensibility
 * Different from:
 * - AssignmentsListPage: Used by teachers for managing assignments
 * - StudentAssignmentsListPage: Used by students/parents for viewing assignments
 */
const AdminStudentAssignments = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { id: studentId } = useParams(); // Get studentId from URL params
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [displayLimit, setDisplayLimit] = useState(10);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [localStudentName, setLocalStudentName] = useState('');
  
  // Get student name from location state or fetch it
  const studentName = location.state?.studentName || 'Student';

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      
      if (!studentId) {
        throw new Error('No student ID provided');
      }

      // Get ALL assignments for the student using admin service (complete cross-class data)
      console.log('Admin fetching ALL assignments for student:', studentId);
      const data = await adminService.getStudentAssignments(studentId);
      console.log('Admin assignments data received:', data);
      
      if (Array.isArray(data)) {
        setAssignments(data);
        
        // If we don't have student name, try to get it from assignment data
        if (!location.state?.studentName && data.length > 0 && data[0].students && data[0].students.length > 0) {
          const firstStudent = data[0].students[0];
          if (typeof firstStudent === 'object' && firstStudent.name) {
            setLocalStudentName(firstStudent.name);
          }
        }
      } else {
        console.error('Expected array but got:', data);
        setAssignments([]);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setError(error.message || 'Failed to load assignments');
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignments();
  }, [studentId]);

  // Scroll event listener for "back to top" button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const filterAssignments = (filter) => {
    setActiveFilter(filter);
  };

  const getFilteredAssignments = () => {
    if (activeFilter === 'all') return assignments;
    
    if (activeFilter === 'pending') {
      return assignments.filter(assignment => 
        assignment.status === 'active' && 
        (!assignment.assessment || !assignment.assessment.rating)
      );
    }
    
    if (activeFilter === 'assessed') {
      return assignments.filter(assignment => 
        assignment.assessment && assignment.assessment.rating
      );
    }
    
    if (activeFilter === 'new-memorization' || activeFilter === 'revision' || activeFilter === 'recitation') {
      return assignments.filter(assignment =>
        assignment.type === activeFilter
      );
    }

    return assignments;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPlanBadgeColors = (type) => {
    switch(type) {
      case 'revision':
        return { backgroundColor: '#f3e5f5', color: '#7227b0' };
      case 'recitation':
        return { backgroundColor: '#e8f5e9', color: '#1b5e20' };
      case 'new-memorization':
      default:
        return { backgroundColor: '#fef8e8', color: '#f57c00' };
    }
  };

  const displayedAssignments = getFilteredAssignments().slice(0, displayLimit);
  const hasMoreAssignments = getFilteredAssignments().length > displayLimit;

  const renderAssignmentCard = (item) => {
    const isAssessed = item.assessment && item.assessment.rating;
    
    return (
      <div key={item._id} className="simple-assignment-card">
        <div className="simple-assignment-header">
          <h3 className="simple-assignment-title">
            {getAssignmentTitle(item)}
          </h3>
        </div>
        
        <div className="simple-assignment-student">
          {/* For admin view, show all classes this assignment covers */}
          Assigned to: {studentName || localStudentName || 'Student'} (All Classes)
        </div>
        
        <div className="simple-assignment-footer">
          <div className="simple-due-date">
            Due: {formatDate(item.dueDate)}
          </div>
          <div className={`simple-status ${
            item.status === 'active' && isPast(item.dueDate) ? 'overdue' :
            item.status === 'active' && isToday(item.dueDate) ? 'due-today' : ''
          }`}>
            {item.status === 'active' && isPast(item.dueDate) ? 'Overdue' :
             item.status === 'active' && isToday(item.dueDate) ? 'Due Today' :
             item.status === 'active' ? 'Active' : item.status}
          </div>
          {item.planId ? (
            <span style={{
              fontSize: '11px',
              padding: '2px 8px',
              ...getPlanBadgeColors(item.type),
              borderRadius: '4px',
              fontWeight: '500',
              textTransform: 'capitalize',
              whiteSpace: 'nowrap'
            }}>
              📋 {item.type.replace('-', ' ')} Plan
            </span>
          ) : (
            <div className="simple-type">
              {item.type === 'new-memorization' ? 'New Memorization' :
               item.type === 'revision' ? 'Revision' :
               item.type === 'recitation' ? 'Recitation' : 'Unknown'}
            </div>
          )}
          {isAssessed && (
            <div className="simple-assessment">
              {item.assessment.rating}
            </div>
          )}
        </div>
        
        {/* Teacher comments section */}
        {isAssessed && item.assessment.comments && (
          <div style={{
            padding: '8px 12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#495057',
            marginTop: '8px',
            borderLeft: '3px solid #007bff'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>Teacher Comments:</div>
            <div style={{ fontStyle: 'italic' }}>"{item.assessment.comments}"</div>
          </div>
        )}
      </div>
    );
  };


  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading assignments...</p>
      </div>
    );
  }

  return (
    <div className="assignments-list-container" id="assignments-container">
      <div className="header">
        <h1>{`${studentName || localStudentName || 'Student'}'s Assignments (Admin View)`}</h1>
        <div style={{ 
          fontSize: '14px', 
          color: '#666', 
          marginTop: '4px',
          padding: '4px 8px',
          backgroundColor: '#fff3cd',
          borderRadius: '4px',
          border: '1px solid #ffeaa7',
          display: 'inline-block'
        }}>
          Admin View - Showing assignments from all classes
        </div>
        
        <div className="header-actions">
          <Link 
            to={`/admin/students/${studentId}`} 
            className="back-link"
          >
            ← Back to Student Profile
          </Link>
        </div>
      </div>

      <div className="filter-bar">
        <div className="filter-chips">
          <button 
            className={`filter-chip ${activeFilter === 'all' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('all')}
          >
            All ({assignments.length})
          </button>
          
          <button 
            className={`filter-chip ${activeFilter === 'pending' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('pending')}
          >
            Pending ({assignments.filter(a => a.status === 'active' && (!a.assessment || !a.assessment.rating)).length})
          </button>
          
          <button 
            className={`filter-chip ${activeFilter === 'assessed' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('assessed')}
          >
            Assessed ({assignments.filter(a => a.assessment && a.assessment.rating).length})
          </button>
          
          <button
            className={`filter-chip ${activeFilter === 'new-memorization' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('new-memorization')}
          >
            New Memorization ({assignments.filter(a => a.type === 'new-memorization').length})
          </button>

          <button
            className={`filter-chip ${activeFilter === 'revision' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('revision')}
          >
            Revision ({assignments.filter(a => a.type === 'revision').length})
          </button>

          <button
            className={`filter-chip ${activeFilter === 'recitation' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('recitation')}
          >
            Recitation ({assignments.filter(a => a.type === 'recitation').length})
          </button>
        </div>
      </div>


      {error && (
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={fetchAssignments} className="retry-button">
            Retry
          </button>
        </div>
      )}

      <div className="assignments-list">
        {displayedAssignments.length > 0 ? (
          displayedAssignments.map(renderAssignmentCard)
        ) : (
          <div className="no-assignments">
            {activeFilter === 'all' ? (
              <p>No assignments found for this student.</p>
            ) : (
              <p>No {activeFilter} assignments found.</p>
            )}
          </div>
        )}
      </div>

      {hasMoreAssignments && (
        <div className="load-more-section">
          <button 
            onClick={() => setDisplayLimit(prev => prev + 10)}
            className="load-more-button"
          >
            Load More ({getFilteredAssignments().length - displayLimit} remaining)
          </button>
        </div>
      )}

      {showScrollTop && (
        <button 
          onClick={scrollToTop}
          className="scroll-to-top-button"
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  );
};

export default AdminStudentAssignments;